/**
 * Xử lý hiển thị / in mã vận đơn Shopee khi API trả JSON (tracking + orderInfo, có thể không có PDF).
 */

function escapeHtml(s) {
  if (s == null || s === '') return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Mở PDF từ chuỗi base64.
 * @param {string} base64
 */
export function openPdfFromBase64(base64) {
  if (!base64 || typeof base64 !== 'string') {
    throw new Error('Không có dữ liệu PDF');
  }
  const binary = atob(base64.replace(/^data:application\/pdf;base64,/, ''));
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
}

function base64ToUint8Array(base64) {
  const normalized = String(base64 || '').replace(
    /^data:application\/pdf;base64,/,
    '',
  );
  const binary = atob(normalized);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function ensurePdfLibLoaded() {
  if (window.PDFLib?.PDFDocument) {
    return Promise.resolve(window.PDFLib);
  }
  if (window.__pdfLibLoadingPromise) return window.__pdfLibLoadingPromise;

  window.__pdfLibLoadingPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src =
      'https://cdn.jsdelivr.net/npm/pdf-lib@1.17.1/dist/pdf-lib.min.js';
    script.async = true;
    script.onload = () => {
      if (window.PDFLib?.PDFDocument) resolve(window.PDFLib);
      else reject(new Error('Không thể khởi tạo thư viện ghép PDF'));
    };
    script.onerror = () =>
      reject(
        new Error(
          'Không thể tải thư viện ghép PDF. Vui lòng kiểm tra mạng hoặc cho phép CDN.',
        ),
      );
    document.head.appendChild(script);
  });
  return window.__pdfLibLoadingPromise;
}

/**
 * Ghép nhiều pdfBase64 thành 1 file PDF và mở để in.
 * @param {Array<string>} pdfBase64List
 */
export async function mergeAndOpenPdfBase64List(pdfBase64List = []) {
  const validList = Array.isArray(pdfBase64List)
    ? pdfBase64List.filter((b64) => !!b64)
    : [];
  if (!validList.length) {
    throw new Error('Không có dữ liệu PDF để ghép');
  }

  const { PDFDocument } = await ensurePdfLibLoaded();
  const mergedPdf = await PDFDocument.create();

  for (const base64 of validList) {
    const srcBytes = base64ToUint8Array(base64);
    const srcPdf = await PDFDocument.load(srcBytes, {
      updateMetadata: false,
      ignoreEncryption: true,
    });
    const pageIndexes = srcPdf.getPageIndices();
    const copiedPages = await mergedPdf.copyPages(srcPdf, pageIndexes);
    copiedPages.forEach((page) => mergedPdf.addPage(page));
  }

  const mergedBytes = await mergedPdf.save({
    useObjectStreams: false,
  });
  const blob = new Blob([mergedBytes], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
}

/**
 * Mở 1 cửa sổ in tổng hợp nhiều phiếu vận đơn.
 * Mỗi phiếu sẽ được nhúng PDF vào 1 ô để in hàng loạt trong cùng 1 lần in.
 * @param {Array} items
 */
export function openBatchTrackingPrintWindow(items = []) {
  const validItems = Array.isArray(items)
    ? items.filter((it) => it?.data?.pdfBase64)
    : [];

  if (!validItems.length) {
    throw new Error('Không có dữ liệu PDF để in hàng loạt');
  }

  const cards = validItems
    .map((it, idx) => {
      const pdfBase64 = String(it.data.pdfBase64).replace(
        /^data:application\/pdf;base64,/,
        '',
      );
      const pdfDataUri = `data:application/pdf;base64,${pdfBase64}`;
      return `
        <section class="label-card">
          <div class="label-title">Đơn #${escapeHtml(it.orderSn || it.orderId || idx + 1)}</div>
          <object class="label-pdf" data="${pdfDataUri}#toolbar=0&navpanes=0&scrollbar=0" type="application/pdf">
            <p>Không thể hiển thị PDF cho đơn ${escapeHtml(it.orderSn || '')}</p>
          </object>
        </section>
      `;
    })
    .join('');

  const html = `<!DOCTYPE html>
  <html>
    <head>
      <meta charset="utf-8">
      <title>In hàng loạt vận đơn</title>
      <style>
        * { box-sizing: border-box; }
        body { margin: 0; font-family: system-ui, sans-serif; background: #f4f6f8; color: #1f2937; }
        .toolbar { position: sticky; top: 0; z-index: 2; background: #fff; border-bottom: 1px solid #e5e7eb; padding: 12px 16px; }
        .toolbar button { padding: 8px 14px; border: none; border-radius: 6px; background: #2563eb; color: #fff; cursor: pointer; }
        .toolbar span { margin-left: 10px; color: #6b7280; font-size: 13px; }
        .grid { padding: 12px; display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
        .label-card { background: #fff; border: 1px solid #d1d5db; border-radius: 8px; overflow: hidden; break-inside: avoid; page-break-inside: avoid; }
        .label-title { padding: 6px 10px; font-size: 12px; font-weight: 600; border-bottom: 1px solid #e5e7eb; background: #f9fafb; }
        .label-pdf { width: 100%; height: 52vh; display: block; border: none; }
        @media print {
          @page { size: A4 portrait; margin: 8mm; }
          body { background: #fff; }
          .toolbar { display: none; }
          .grid { padding: 0; gap: 6mm; grid-template-columns: 1fr 1fr; }
          .label-card { border: none; border-radius: 0; }
          .label-title { display: none; }
          .label-pdf { height: 135mm; }
        }
      </style>
    </head>
    <body>
      <div class="toolbar">
        <button onclick="window.print()">In tất cả</button>
        <span>${validItems.length} phiếu vận đơn</span>
      </div>
      <main class="grid">${cards}</main>
    </body>
  </html>`;

  const w = window.open('', '_blank');
  if (!w) {
    throw new Error('Trình duyệt đã chặn cửa sổ mới. Vui lòng cho phép popup.');
  }
  w.document.write(html);
  w.document.close();
}

/**
 * Cửa sổ in preview: trackingNumber + orderInfo (khi documentInfo/documentData null).
 * @param {object} data — payload từ GET print-tracking (data inner)
 */
export function openTrackingPrintPreviewWindow(data) {
  const info = data?.orderInfo || {};
  const items = Array.isArray(info.items) ? info.items : [];
  const rows = items
    .map(
      (it) =>
        `<tr><td>${escapeHtml(it.name)}</td><td>${escapeHtml(it.sku)}</td><td>${escapeHtml(
          String(it.quantity ?? ''),
        )}</td></tr>`,
    )
    .join('');

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>In vận đơn</title>
  <style>
    body { font-family: system-ui, sans-serif; padding: 16px; max-width: 720px; margin: 0 auto; }
    h1 { font-size: 1.1rem; }
    .meta { margin: 12px 0; line-height: 1.6; }
    table { width: 100%; border-collapse: collapse; margin-top: 12px; font-size: 13px; }
    th, td { border: 1px solid #ccc; padding: 8px; text-align: left; }
    th { background: #f5f5f5; }
    @media print { body { padding: 0; } }
  </style></head><body>
  <h1>Thông tin giao hàng</h1>
  <div class="meta">
    <div><strong>Mã đơn sàn:</strong> ${escapeHtml(data?.orderSn)}</div>
    <div><strong>Mã vận đơn:</strong> ${escapeHtml(data?.trackingNumber) || '<em>Chưa có / chờ Shopee</em>'}</div>
    <div><strong>Người nhận:</strong> ${escapeHtml(info.recipientName)}</div>
    <div><strong>SĐT:</strong> ${escapeHtml(info.recipientPhone)}</div>
    <div><strong>Địa chỉ:</strong> ${escapeHtml(info.recipientAddress)}</div>
    <div>${escapeHtml([info.ward, info.district, info.city].filter(Boolean).join(', '))}</div>
  </div>
  <table><thead><tr><th>Sản phẩm</th><th>SKU</th><th>SL</th></tr></thead><tbody>${rows || '<tr><td colspan="3">—</td></tr>'}</tbody></table>
  <script>window.onload=function(){window.print();}</script>
  </body></html>`;

  const w = window.open('', '_blank');
  if (!w) {
    throw new Error('Trình duyệt đã chặn cửa sổ mới. Vui lòng cho phép popup.');
  }
  w.document.write(html);
  w.document.close();
}

/**
 * Chuẩn hóa response GET print-tracking thành object data bên trong.
 * @param {object} apiResponse — axios response.data
 */
export function unwrapPrintTrackingPayload(apiResponse) {
  if (!apiResponse) return null;
  const inner = apiResponse.data !== undefined ? apiResponse.data : apiResponse;
  return inner;
}

/**
 * Xử lý response print-tracking / print-tracking/pdf: mở PDF, URL hoặc cửa sổ preview.
 * @throws {Error}
 */
export function processPrintTrackingApiResponse(apiResponse) {
  if (apiResponse?.success === false) {
    throw new Error(
      apiResponse.error ||
        apiResponse.message ||
        'Không thể lấy dữ liệu in vận đơn',
    );
  }
  const d = unwrapPrintTrackingPayload(apiResponse);
  if (!d || typeof d !== 'object') {
    throw new Error('Dữ liệu in không hợp lệ');
  }
  if (d.pdfBase64) {
    openPdfFromBase64(d.pdfBase64);
    return 'pdf';
  }
  if (d.url && typeof d.url === 'string') {
    window.open(d.url, '_blank');
    return 'url';
  }
  openTrackingPrintPreviewWindow(d);
  return 'preview';
}
